import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { updateFAQ, clearFAQState } from "../../redux/Slices/FAQSlice";
import { toast } from "react-toastify";
import { validateFieldLanguage } from "../../Utils/languageValidation";

const EditFAQ = ({ faq, onSuccess }) => {
  const { t, i18n } = useTranslation("global");
  const dispatch = useDispatch();

  const { updateLoading, updateError, updateSuccessMessage } = useSelector((state) => state.faqs);

  const [formData, setFormData] = useState({
    q_ar: "",
    q_en: "",
    a_ar: "",
    a_en: "",
    status: 1,
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (faq) {
      const original = {
        q_ar: faq.question_ar || "",
        q_en: faq.question_en || "",
        a_ar: faq.answer_ar || "",
        a_en: faq.answer_en || "",
        status: (faq.status === 1 || faq.status === '1' || faq.status === 'active') ? 1 : 0,
      };
      setOriginalData(original);
      setFormData(original);
    }
  }, [faq]);

  useEffect(() => {
    if (originalData) {
      const hasFormChanges = 
        formData.q_ar !== originalData.q_ar ||
        formData.q_en !== originalData.q_en ||
        formData.a_ar !== originalData.a_ar ||
        formData.a_en !== originalData.a_en ||
        formData.status !== originalData.status;
      
      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.q_ar.trim() || !formData.a_ar.trim() || !formData.q_en.trim() || !formData.a_en.trim()) {
      toast.error("يرجى ملء جميع الحقول المطلوبة باللغتين العربية والإنجليزية");
      return;
    }

    // BUG-60 fix: التحقق من لغة الحقول (استخدام الأداة المشتركة بدلاً من التحقق المحلي)
    const qArError = validateFieldLanguage(formData.q_ar, "ar");
    if (qArError) return toast.error(qArError);
    const aArError = validateFieldLanguage(formData.a_ar, "ar");
    if (aArError) return toast.error(aArError);
    const qEnError = validateFieldLanguage(formData.q_en, "en");
    if (qEnError) return toast.error(qEnError);
    const aEnError = validateFieldLanguage(formData.a_en, "en");
    if (aEnError) return toast.error(aEnError);

    const submitData = {
      ...formData,
      status: parseInt(formData.status)
    };
    dispatch(updateFAQ({ id: faq.id, faqData: submitData }));
  };

  useEffect(() => {
    if (updateSuccessMessage) {
      toast.success(updateSuccessMessage);
      if (onSuccess) onSuccess();
      dispatch(clearFAQState());
    }
    if (updateError) {
      toast.error(updateError);
      dispatch(clearFAQState());
    }
  }, [updateSuccessMessage, updateError, onSuccess, dispatch]);

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: i18n.language === 'ar' ? 'right' : 'left'
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--warning-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(251, 191, 36, 0.15)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <form onSubmit={handleSubmit} style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
      <style>{`
        .faq-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .faq-custom-select:focus {
          border-color: var(--warning-color) !important;
          box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.15) !important;
        }
      `}</style>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>{t("labels.question")} ({t("labels.arabic")}) *</label>
          <textarea
            name="q_ar"
            value={formData.q_ar}
            onChange={handleChange}
            required
            rows="2"
            placeholder={t("placeholders.enterArabicQuestion")}
            style={{ ...inputStyle, resize: "vertical" }}
            onFocus={focusHandler}
            onBlur={blurHandler}
          />
        </div>

        <div>
          <label style={labelStyle}>{t("labels.question")} ({t("labels.english")}) *</label>
          <textarea
            name="q_en"
            value={formData.q_en}
            onChange={handleChange}
            required
            rows="2"
            placeholder={t("placeholders.enterEnglishQuestion")}
            style={{ ...inputStyle, resize: "vertical" }}
            onFocus={focusHandler}
            onBlur={blurHandler}
            dir="ltr"
          />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>{t("labels.answer")} ({t("labels.arabic")}) *</label>
          <textarea
            name="a_ar"
            value={formData.a_ar}
            onChange={handleChange}
            required
            rows="3"
            placeholder={t("placeholders.enterArabicAnswer")}
            style={{ ...inputStyle, resize: "vertical" }}
            onFocus={focusHandler}
            onBlur={blurHandler}
          />
        </div>

        <div>
          <label style={labelStyle}>{t("labels.answer")} ({t("labels.english")}) *</label>
          <textarea
            name="a_en"
            value={formData.a_en}
            onChange={handleChange}
            required
            rows="3"
            placeholder={t("placeholders.enterEnglishAnswer")}
            style={{ ...inputStyle, resize: "vertical" }}
            onFocus={focusHandler}
            onBlur={blurHandler}
            dir="ltr"
          />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>{t("labels.status")} *</label>
        <select
          className="faq-custom-select"
          name="status"
          value={formData.status}
          onChange={handleChange}
          required
        >
          <option value={1}>🟢 {t("labels.active")}</option>
          <option value={0}>🔴 {t("labels.inactive")}</option>
        </select>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          type="button"
          onClick={onSuccess}
          style={{
            padding: "10px 20px",
            borderRadius: 9,
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "#475569",
            fontWeight: 600,
            cursor: "pointer",
            fontSize: 14
          }}
        >
          إلغاء
        </button>

        <button
          type="submit"
          disabled={updateLoading || !hasChanges}
          style={{
            padding: "10px 28px",
            borderRadius: 10,
            border: "none",
            background: updateLoading ? "var(--warning-color)" : !hasChanges ? "#cbd5e1" : "linear-gradient(135deg,#f59e0b,var(--warning-color))",
            color: "#fff",
            fontWeight: 700,
            cursor: (updateLoading || !hasChanges) ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14
          }}
        >
          {updateLoading ? (
            <>
              <span className="spinner-border spinner-border-sm" />
              {t("labels.loading")}
            </>
          ) : (
            <>{t("btns.updateQuestion")}</>
          )}
        </button>
      </div>
    </form>
  );
};

export default EditFAQ;
